import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles, ScrollText, Compass, Gem, Flame } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';

/**
 * Sample Answer Glimpse — v3 (Feb 20, 2026 Final Luxury Polish)
 *
 * Delta vs v2:
 *  - Locked cards get real hint content (what's actually inside)
 *  - Subtle shimmer sweep animation across locked cards
 *  - Gold icons in circular badges above lock (Gem, Flame, Compass)
 *  - Bigger hero card with dual-column bento (verdict + reasoning)
 *  - Motion curve = Apple luxury out-quart
 */
export default function SampleAnswerGlimpse() {
  const easeLux = [0.22, 1, 0.36, 1];
  const { lang } = useLang();
  const isHi = lang !== 'en';

  return (
    <section
      className="relative py-16 lg:py-24"
      data-testid="sample-answer-glimpse"
    >
      {/* Subtle royal-purple base ambience */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050505] to-transparent pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[#3d101e]/25 blur-[200px] pointer-events-none" />

      <div className="container-custom max-w-6xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: easeLux }}
          className="text-center mb-16 lg:mb-24"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
            <span className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37] font-medium">
              {isHi ? 'आपके उत्तर का प्रारूप' : 'Your Answer Format'}
            </span>
            <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
          </div>
          <h2 className="font-heading text-[36px] sm:text-5xl lg:text-[56px] leading-[1.05] font-light text-[#F5F5F5] tracking-[-0.02em] mb-6">
            {isHi ? (
              <>
                आपको मिलने वाले <span className="text-gold-gradient font-normal">उत्तर</span>
                <br />
                की झलक
              </>
            ) : (
              <>
                Preview of Your <span className="text-gold-gradient font-normal">Personalized</span>
                <br />
                Vedic Answer
              </>
            )}
          </h2>
          <p className="text-[#B5B5B5] text-[15px] sm:text-[17px] font-light max-w-2xl mx-auto leading-[1.85]">
            {isHi
              ? 'यह केवल उत्तर के प्रारूप की झलक है। आपका वास्तविक उत्तर आपकी जन्म-कुंडली, ग्रह स्थिति एवं पूछे गए प्रश्न के आधार पर व्यक्तिगत रूप से तैयार किया जाएगा।'
              : 'This is a preview of the answer format. Your actual consultation will be personally prepared based on your birth chart, planetary positions, and your specific question.'}
          </p>

          {/* Lotus divider */}
          <div className="relative flex items-center justify-center mt-12 max-w-md mx-auto">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
            <div className="mx-4 relative">
              <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
              <div className="absolute inset-0 w-2 h-2 rotate-45 bg-[#D4AF37] blur-sm opacity-70" />
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
          </div>
        </motion.div>

        {/* Preview question label — no "example/sample" wording */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: easeLux, delay: 0.15 }}
          className="text-center mb-10 lg:mb-14"
        >
          <p className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#8A8A8A] mb-4 font-light">
            <span className="w-6 h-px bg-[#D4AF37]/50" />
            {isHi ? 'प्रश्न प्रारूप' : 'Question Format'}
            <span className="w-6 h-px bg-[#D4AF37]/50" />
          </p>
          <p className="font-heading text-2xl sm:text-3xl lg:text-[36px] text-[#F5F5F5] font-light leading-tight tracking-tight">
            {isHi ? '“मेरी शादी कब होगी?”' : '“When will I get married?”'}
          </p>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {/* HERO card — spans full row on md+ */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: easeLux }}
            className="md:col-span-3 relative bg-gradient-to-br from-[#0C0C0C] via-[#0A0A0A] to-[#080808] border border-[#D4AF37]/22 rounded-[28px] p-8 sm:p-12 overflow-hidden shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9),0_0_80px_-40px_rgba(212,175,55,0.18)]"
          >
            {/* Corner yantra ornament */}
            <div className="absolute -top-8 -right-8 opacity-[0.08] pointer-events-none">
              <svg width="180" height="180" viewBox="0 0 100 100" className="text-[#D4AF37]">
                <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="0.5" />
                <circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" strokeWidth="0.5" />
                <circle cx="50" cy="50" r="22" fill="none" stroke="currentColor" strokeWidth="0.5" />
                {Array.from({ length: 8 }).map((_, i) => (
                  <line key={i} x1="50" y1="50" x2="50" y2="4" stroke="currentColor" strokeWidth="0.35"
                    transform={`rotate(${(i * 360) / 8} 50 50)`} />
                ))}
              </svg>
            </div>

            {/* Top label */}
            <div className="flex items-center gap-2.5 mb-8">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/25 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.4} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-medium">
                पराशरीय उत्तर
              </span>
            </div>

            {/* 2-column layout: shloka + verdict */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
              {/* LEFT: Shloka + explanation */}
              <div>
                <p className="font-heading text-[20px] sm:text-[22px] lg:text-[24px] text-gold-gradient italic leading-[1.95] mb-3 tracking-tight">
                  सप्तमे शुभ संयोगे<br />
                  विवाह योगो भविष्यति॥
                </p>
                <p className="text-[#7A7A7A] text-[11px] mb-8 tracking-[0.15em] uppercase">
                  — फलदीपिका, अध्याय 9.17
                </p>
                <p className="text-[#B5B5B5] text-[14.5px] leading-[1.9] font-light max-w-md">
                  सप्तम भाव पर गुरु-शुक्र की शुभ दृष्टि है। महादशा गुरु + अन्तर्दशा शुक्र
                  विवाह को विशेष सहयोग देते हैं। गोचर में लाभ-भाव पर शुभ प्रभाव — यह अवधि
                  सबसे अनुकूल रहेगी।
                </p>
              </div>

              {/* RIGHT: Verdict panel */}
              <div className="relative pl-0 lg:pl-10 lg:border-l lg:border-[#D4AF37]/12">
                <p className="text-[10.5px] uppercase tracking-[0.4em] text-[#D4AF37]/85 mb-4 font-medium">
                  Primary Timing Window
                </p>
                <h3 className="font-heading text-[32px] sm:text-[38px] lg:text-[42px] text-[#F5F5F5] font-light leading-[1.05] tracking-tight mb-3">
                  फरवरी 2028
                  <br />
                  → जून 2028
                </h3>
                <p className="text-[15px] text-[#D4AF37]/90 font-light mb-8">
                  प्रबल विवाह-योग
                </p>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-[10.5px] uppercase tracking-[0.28em] text-[#8A8A8A] font-light">
                    Success Probability
                  </span>
                </div>
                <div className="text-4xl text-[#F5F5F5] font-light tracking-tight">
                  64<span className="text-lg text-[#D4AF37]/70">%</span>
                </div>
              </div>
            </div>

            {/* Meta strip */}
            <div className="mt-10 pt-7 border-t border-white/[0.05] flex flex-wrap gap-x-8 gap-y-3 text-[11px] text-[#8A8A8A] tracking-[0.12em]">
              <span className="flex items-center gap-2"><Compass className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.4} /> Swiss Ephemeris</span>
              <span className="flex items-center gap-2"><ScrollText className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.4} /> 12-कारक विश्लेषण</span>
              <span className="flex items-center gap-2">⚡ 60 सेकंड में</span>
            </div>
          </motion.div>

          {/* Locked cards — 3 columns */}
          <LockedCard
            delay={0.15}
            icon={Gem}
            title="व्यावहारिक उपाय"
            teaser="मंत्र · वार-रत्न · दान क्रम · Personalized"
            preview={['🕉 गुरु बीज मंत्र', '💎 पुखराज रत्न', '📅 शुभ मुहूर्त तालिका']}
          />
          <LockedCard
            delay={0.2}
            icon={Flame}
            title="ग्रह-बल विवेचना"
            teaser="Shad-Bala · Vimshopaka Bala"
            preview={['⚡ गुरु — बलवान', '🔥 शुक्र — मध्यम', '☾ चन्द्र — कमजोर']}
          />
          <LockedCard
            delay={0.25}
            icon={Compass}
            title="विस्तृत Timing Chart"
            teaser="Primary + 2 वैकल्पिक windows"
            preview={['📆 फरवरी 2028', '🌗 अक्टूबर 2026', '🌙 दिसंबर 2027']}
          />
        </div>

        {/* Bottom whisper */}
        <p className="text-center text-[11px] text-[#6E6E6E] tracking-[0.35em] uppercase mt-16 font-light">
          {isHi ? 'व्यक्तिगत उत्तर पाने के लिए · ₹99 में प्रारंभ करें' : 'Begin Your Personalized Consultation · from ₹99'}
        </p>
      </div>
    </section>
  );
}

const LockedCard = ({ icon: Icon = Lock, title, teaser, preview = [], delay = 0 }) => {
  const easeLux = [0.22, 1, 0.36, 1];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1.1, ease: easeLux, delay }}
      className="group relative bg-gradient-to-br from-[#0A0A0A] via-[#0B0B0B] to-[#080808] backdrop-blur-xl border border-white/[0.06] rounded-[28px] p-8 lg:p-10 flex flex-col overflow-hidden shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)] hover:border-[#D4AF37]/22 transition-colors duration-700"
    >
      {/* Shimmer sweep */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            'linear-gradient(105deg, transparent 40%, rgba(212,175,55,0.15) 50%, transparent 60%)',
        }}
        animate={{ x: ['-50%', '150%'] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
      />

      {/* Top: gold icon badge */}
      <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37]/[0.14] to-transparent border border-[#D4AF37]/25 flex items-center justify-center mb-6">
        <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
      </div>

      {/* Title + teaser */}
      <h3 className="font-heading text-[19px] text-[#F5F5F5] font-normal leading-tight tracking-tight mb-1.5">
        {title}
      </h3>
      <p className="text-[11.5px] text-[#8A8A8A] tracking-wider mb-6 font-light">
        {teaser}
      </p>

      {/* Blurred preview hints */}
      <div className="space-y-2 mb-6 flex-1">
        {preview.map((p, i) => (
          <div
            key={i}
            className="text-[12.5px] text-[#B5B5B5]/50 font-light tracking-wide select-none"
            style={{ filter: 'blur(3px)' }}
          >
            {p}
          </div>
        ))}
      </div>

      {/* Bottom lock chip */}
      <div className="flex items-center justify-between pt-5 border-t border-white/[0.05]">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#D4AF37]/12 flex items-center justify-center">
            <Lock className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.6} />
          </div>
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#8A8A8A] font-medium">
            Locked
          </span>
        </div>
        <span className="text-[10px] text-[#D4AF37]/85 font-medium tracking-wider">
          ₹99 में unlock
        </span>
      </div>
    </motion.div>
  );
};
